import {
  Component,
  DestroyRef,
  effect,
  input,
  output,
} from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { distinctUntilChanged, filter, firstValueFrom, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService } from '../../../../../../../services/message.service';
import {
  CrawlUrlsService,
  CrawlUrlsFilters,
} from '../../../../../../../services/crawl-urls.service';
import {
  IntegrationService,
} from '../../../../../../../services/integration.service';
import {
  CrawlUrl,
  CrawlerExecutionSummary,
  CrawlerUrlStatus,
  CrawlUrlDocumentRef,
} from '../../../../../../../models/crawlerUrl';
import {
  Option,
} from '../../../../../../../components/select/select.component';
import {
  PaginatedListManager,
} from '../../../../../../../shared/single-crawler/paginated-list.manager';
import {
  Crawler,
  CrawlerLaunchEligibility,
  CrawlerStatus,
  CrawlerType,
} from '../../../../../../../models/integration/crawler';
import {
  LaunchEligibilityPoller,
  LaunchPending,
  isCrawlerActive,
} from '../../../../../../../shared/single-crawler/launch-eligibility';
import { Paginator } from '../../../../../../../models/paginator';
import { DiscoveredUrl } from '../../../../../../../models/discoveredUrls';
import { UtilsService } from '../../../../../../../services/utils.service';
import { AclService } from '../../../../../../../services/acl.service';
import { ConfirmationDialogComponent } from '../../../../../../../dialogs/confirmation-dialog/confirmation-dialog.component';

// What a client reads for a failed URL: the raw error (Azure messages, request ids) is for the platform team only.
const FAILURE_REASONS: ReadonlyArray<[RegExp, string]> = [
  [/URL not allowed/i, 'NOT_ALLOWED'],
  [/No result after|Not started after/i, 'NO_RESULT'],
  [/timeout|timed out/i, 'TIMEOUT'],
  [/NS_ERROR_UNKNOWN_HOST|ERR_NAME_NOT_RESOLVED|ENOTFOUND|getaddrinfo/i, 'NOT_FOUND'],
  [/CONNECTION_REFUSED|ECONNREFUSED|NET_RESET|ECONNRESET|NET_INTERRUPT/i, 'UNREACHABLE'],
];

// crawl step shown for both discovery and crawl-only integrations, listing crawled urls
@Component({
  selector: 'app-single-crawler-crawl-step',
  templateUrl: './single-crawler-crawl-step.component.html',
  styleUrls: ['./single-crawler-crawl-step.component.scss'],
})
export class SingleCrawlerCrawlStepComponent {
  readonly integrationId = input.required<string>();
  readonly crawler = input.required<Crawler>();
  // named in the cancellation confirmation
  readonly integrationName = input<string>('');
  // single poller shared (one HTTP call drives gates, list refresh, etc.)
  readonly eligibilityPoller = input.required<LaunchEligibilityPoller>();
  readonly crawlLaunched = output<void>();

  protected readonly CrawlerType = CrawlerType;

  executions: CrawlerExecutionSummary[] = [];
  executionOptions: Option[] = [];
  launching = false;
  // which button started the run in progress, so that only that one spins
  private launchedMode?: 'all' | 'failed';

  get eligibility(): CrawlerLaunchEligibility | undefined {
    return this.eligibilityPoller().eligibility;
  }

  get launchPending(): LaunchPending {
    return this.eligibilityPoller().launchPending;
  }

  readonly statusSelectOptions: Option[] = ([
    'pending',
    'success',
    'failed',
  ] as CrawlerUrlStatus[])
    .map((status) => ({
      label: `INTEGRATIONS.CRAWL_URLS.STATUSES.${status}`,
      value: status,
      selected: false,
      disabled: false,
    }));

  readonly displayedColumns: string[] = [
    'url',
    'status',
    'document',
    'error',
    'processedAt',
  ];

  readonly list: PaginatedListManager<CrawlUrl, CrawlUrlsFilters>;

  constructor(
    private readonly _message: MessageService,
    private readonly crawlUrlsService: CrawlUrlsService,
    private readonly integrationService: IntegrationService,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly utils: UtilsService,
    private readonly destroyRef: DestroyRef,
    private readonly dialog: MatDialog,
    private readonly aclService: AclService,
  ) {
    this.list = new PaginatedListManager<CrawlUrl, CrawlUrlsFilters>({
      destroyRef,
      initialFilters: {
        query: undefined,
        status: undefined,
        executionId: undefined,
      },
      retrieveFn: (page, limit, offset, sort, filters) =>
        this.crawlUrlsService.retrieveCrawlUrlsPaginated(
          this.integrationId(),
          page,
          limit,
          offset,
          sort,
          filters,
        ),
      onError: (err) => this.utils.toastError(err),
      onLoadingStart: () => this._message.emitChange(
        'LOADING',
        'START',
      ),
      onLoadingEnd: () => this._message.emitChange(
        'LOADING',
        'END',
      ),
    });

    effect(() => {
      if (this.integrationId()) {
        void this.loadExecutionsAndInit();
        this.list.startAutoRefresh(
          this.eligibilityPoller().eligibility$.pipe(
            map((eligibility) => isCrawlerActive(eligibility.status)),
          ),
        );
        // the reminder of the pages not captured follows the end of each capture
        this.eligibilityPoller().eligibility$.pipe(
          map((eligibility) => isCrawlerActive(eligibility.status)),
          distinctUntilChanged(),
          filter((active) => !active),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(() => void this.loadNotCaptured());
      }
    });
  }

  private async loadExecutionsAndInit(): Promise<void> {
    await this.loadExecutions();
    await this.list.init();
    await this.loadNotCaptured();
  }

  // Pages found that no capture took, shown read-only under the captures: they are chosen in the discovery step.
  notCaptured?: Paginator<DiscoveredUrl>;
  readonly notCapturedShown = 100;

  async loadNotCaptured(): Promise<void> {
    if (this.crawler().type !== CrawlerType.DISCOVERY) return;
    try {
      this.notCaptured = await this.crawlUrlsService.retrieveNotCaptured(this.integrationId(), this.notCapturedShown);
    } catch {
      this.notCaptured = undefined;
    }
  }

  private async loadExecutions(): Promise<void> {
    try {
      this.executions = await this.crawlUrlsService.listExecutions(this.integrationId());
      // Default to the latest execution (list is sorted newest-first server-side).
      this.list.filters.executionId = this.executions[0]?._id;
      this.executionOptions = this.executions.map((execution) => ({
        label: this.executionLabel(execution),
        value: execution._id,
        selected: execution._id === this.list.filters.executionId,
        disabled: false,
      }));
    } catch {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    }
  }

  private executionLabel(execution: CrawlerExecutionSummary): string {
    const date = execution.triggeredAt
      ? moment(execution.triggeredAt)
        .format('YYYY-MM-DD HH:mm')
      : '';
    const status = execution.progress?.status ?? '';
    return `#${execution.executionIndex}${status ? ' · ' + status : ''}${date
      ? ' · ' + date
      : ''}`;
  }

  async handlePage(event: PageEvent): Promise<void> {
    await this.list.handlePage(event);
  }

  async onSortChange(event: Sort): Promise<void> {
    await this.list.onSortChange(event);
  }

  onQueryChange(query?: string): void {
    this.list.updateFilters({ query });
  }

  onStatusChange(event?: { value: Option }): void {
    this.list.updateFilters({ status: event?.value?.value });
  }

  async onExecutionChange(event?: { value: Option }): Promise<void> {
    await this.list.updateFiltersNow({ executionId: event?.value?.value });
  }

  date(value?: string): string {
    return value ?
      moment(value).format('YYYY-MM-DD HH:mm')
      : '-';
  }

  // documentId is populated to an object only when a document was produced.
  documentRef(crawlUrl: CrawlUrl): CrawlUrlDocumentRef | undefined {
    const doc = crawlUrl.documentId;
    return doc && typeof doc === 'object' ? doc : undefined;
  }

  openDocument(crawlUrl: CrawlUrl): void {
    const doc = this.documentRef(crawlUrl);
    if (doc?._id) {
      void this.router.navigate(['documents', doc._id]);
    }
  }

  async launchCrawl(retryMode: boolean = false): Promise<void> {
    try {
      this.launching = true;
      this.launchedMode = retryMode ? 'failed' : 'all';
      this.launchPending.mark();
      await this.integrationService.trigger(
        this.integrationId(),
        retryMode,
        'crawl',
      );
      this.toastr.success(this.translate.instant('INTEGRATIONS.CRAWL_URLS.CRAWL_LAUNCHED'));
      this.crawlLaunched.emit();
      await this.loadExecutions();
      await this.list.init();
    } catch (err) {
      this.launchPending.clear();
      this.utils.toastError(err);
    } finally {
      this.launching = false;
    }
  }

  async cancelCrawl(): Promise<void> {
    if (!this.canCancelCrawl || !this.eligibility?.runningExecutionId) return;
    // The button shows up where the launch button just was: a double click must not cancel what it launched.
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'INTEGRATIONS.CANCEL_CRAWLER_TITLE',
        text: this.translate.instant('INTEGRATIONS.CANCEL_CRAWLER_TEXT', { name: this.integrationName() }),
        button: { text: 'INTEGRATIONS.CRAWL_URLS.CANCEL_TOOLTIP', class: 'danger-button' },
      },
    };
    const result: { confirmed: boolean } = await firstValueFrom(
      this.dialog.open(ConfirmationDialogComponent, config).afterClosed(),
    );
    if (!result?.confirmed || !this.canCancelCrawl || !this.eligibility?.runningExecutionId) return;
    try {
      await this.integrationService.cancelExecution(
        this.crawler()._id,
        this.eligibility.runningExecutionId,
      );
      this.toastr.success(this.translate.instant('INTEGRATIONS.CANCEL_CRAWLER_SUCCESS'));
      this.launchPending.clear();
    } catch {
      this.toastr.error(this.translate.instant('ERRORS.GENERIC'));
    }
  }

  // Translation key of the reason shown to the client for a failed URL.
  failureReason(crawlUrl: CrawlUrl): string {
    const match = FAILURE_REASONS.find(([pattern]) => pattern.test(crawlUrl.error ?? ''));
    if (match) return `INTEGRATIONS.CRAWL_URLS.FAILURES.${match[1]}`;
    return crawlUrl.errorPhase === 'post-crawl'
      ? 'INTEGRATIONS.CRAWL_URLS.FAILURES.PROCESSING'
      : 'INTEGRATIONS.CRAWL_URLS.FAILURES.CAPTURE';
  }

  // The raw error helps the platform team diagnose; a client gets the reason only.
  canReadRawErrors(): boolean {
    return this.aclService.hasPlatformRights();
  }

  canLaunch(): boolean {
    return !!this.eligibility?.canRunCrawl && !this.launching;
  }

  get canCancelCrawl() {
    return (this.eligibility?.isCrawlRunning && !this.eligibility?.canRunCrawl) || this.launchPending.isPending;
  }

  protected isCrawlingAll(): boolean {
    return this.isCrawling() && this.launchedMode !== 'failed';
  }

  protected isRetryingFailed(): boolean {
    return this.isCrawling() && this.launchedMode === 'failed';
  }

  protected isCrawling(): boolean {
    const status = this.eligibility?.status;
    if (!status) {
      return this.launchPending.isPending || false;
    }
    return this.launchPending.isPending ||
      [CrawlerStatus.CRAWLING, CrawlerStatus.UPLOADING].includes(status);
  }
}
